import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ConfiguratorPresetGroupDto } from '@/services/configurator';
import { useOptionConfigurator } from '../../OptionConfiguratorContext';
import { styleGroupsForRoom } from './styleGroupsForRoom';

export interface StyleWalkthrough {
    /** The style groups applicable to the current room (room-scoped first, then global). */
    groups: ConfiguratorPresetGroupDto[];
    /**
     * A required style group (forceSelection) still has no pick — product selection should be gated
     * behind a "choose your style" prompt until the user resolves it.
     */
    pending: boolean;
    /** The groups currently shown in the modal (live objects), or null when the modal is closed. */
    walkGroups: ConfiguratorPresetGroupDto[] | null;
    /** Open the walkthrough over every unpicked group (used by the gate's CTA). */
    start: () => void;
    /** Open the modal on a single group (manual click on a style card/row). */
    openGroup: (group: ConfiguratorPresetGroupDto) => void;
    /** Close the modal. */
    close: () => void;
}

/**
 * Shared state for the room's style-selection modal, used by the card layout (`RoomStyleCardSection`),
 * the list layout (`RoomStyleSection`) and the gate (`StyleSelectionGate`).
 *
 * A room whose required style groups aren't all picked is considered `pending`: the option menu is
 * replaced by a prompt asking the user to choose a style first (see `StyleSelectionGate`). Picking is
 * done through the walkthrough modal — `start()` walks every unpicked group in sequence; `openGroup`
 * opens just one.
 */
export function useStyleWalkthrough(): StyleWalkthrough {
    const { roomId } = useParams<{ roomId: string }>();
    const { configuratorData, rooms } = useOptionConfigurator();
    // Ordered ids of the style groups the modal is currently walking (null = closed).
    const [walkIds, setWalkIds] = useState<number[] | null>(null);

    const currentRoomId = Number(roomId);

    const groups = useMemo(() => {
        const roomGroups = rooms.find((r) => r.roomId === currentRoomId)?.groups ?? [];
        return styleGroupsForRoom(configuratorData?.styleGroups ?? [], roomGroups, currentRoomId);
    }, [configuratorData?.styleGroups, rooms, currentRoomId]);

    const isPicked = (g: ConfiguratorPresetGroupDto) => g.styles.some((s) => s.isPicked);
    // Gate only on *required* groups so optional styles never trap the user on the prompt.
    const pending = groups.some((g) => g.forceSelection && !isPicked(g));

    // Resolve the walk ids back to live group objects (so the modal sees fresh `isPicked` state after
    // a pick). Filtering by the current room's groups also drops stale ids after a room change.
    const walkGroups = useMemo(() => {
        if (!walkIds) return null;
        return walkIds
            .map((id) => groups.find((g) => g.id === id))
            .filter((g): g is ConfiguratorPresetGroupDto => !!g);
    }, [walkIds, groups]);

    return {
        groups,
        pending,
        walkGroups,
        start: () => setWalkIds(groups.filter((g) => !isPicked(g)).map((g) => g.id)),
        openGroup: (group) => setWalkIds([group.id]),
        close: () => setWalkIds(null),
    };
}
